import { config } from "@config/config";
import { TextWrapper } from "@shared/components/blog/text-wrapper";
import { CookieConsentFormContainer } from "@shared/components/cookie-consent/form/cookie-consent-modal-form-container";
import { Helmet } from "@shared/components/helmet/helmet";
import { NavBar } from "@shared/components/navbar/navbar";
import { UxfCookiesConsentType } from "@shared/lib/cookie-consent/cookie-consent.types";
import { setCookieConsent } from "@shared/lib/cookie-consent/set-cookie-consent";
import { openModal } from "@shared/services/modal-service";
import { useLocale } from "@translations/locale-context";
import { Button } from "@ui/components/button/button";
import { Footer } from "@ui/components/footer/footer";
import { TextLink } from "@ui/components/text-link/text-link";
import { NextPage } from "next";
import { Trans, useTranslation } from "next-i18next";
import React, { CSSProperties, useCallback } from "react";

export const Page: NextPage = () => {
    const locale = useLocale();
    const { t } = useTranslation();

    const setConsentData = useCallback(
        (consentData: UxfCookiesConsentType) => () => {
            setCookieConsent(consentData);
        },
        [],
    );

    return (
        <>
            <Helmet
                canonicalUrl={
                    config.FRONTEND_URL + (locale === "en" ? "/en/privacy-policy" : "/ochrana-osobnich-udaju")
                }
                metaTagData={{
                    metaTitle: t("helmet.privacyPolicy.title"),
                    metaDescription: t("helmet.privacyPolicy.description"),
                }}
                title={t("helmet.privacyPolicy.title")}
            />
            <NavBar />
            <main className="flex flex-col items-center bg-uxfVioletLightest">
                <div className="ui-container w-fit space-y-20 py-16 md:space-y-28 md:py-24">
                    <div className="space-y-12 md:space-y-16">
                        <h1 className="ui-typo-h1">{t("privacyPolicy.title")}</h1>
                        <TextWrapper className="space-y-8">
                            {t<string, string[]>("privacyPolicy.baseInfo", { returnObjects: true }).map(
                                (text, index) => (
                                    <p key={index}>
                                        {text.includes("<email>") ? (
                                            <Trans
                                                as="p"
                                                key={index}
                                                i18nKey={text}
                                                values={{
                                                    email: "hi@uxf.cz",
                                                }}
                                                components={{
                                                    email: <TextLink href="mailto:hi@uxf.cz" />,
                                                }}
                                            />
                                        ) : (
                                            text
                                        )}
                                    </p>
                                ),
                            )}
                        </TextWrapper>
                        <TextWrapper>
                            <p>{t("privacyPolicy.rulesList.title")}</p>
                            <ul>
                                {t<string, string[]>("privacyPolicy.rulesList.list", {
                                    returnObjects: true,
                                }).map((text, index) => (
                                    <li key={index}>{text}</li>
                                ))}
                            </ul>
                        </TextWrapper>
                    </div>
                    <div className="space-y-6">
                        <h2 className="ui-typo-super-large">{t("privacyPolicy.statement.title")}</h2>
                        <TextWrapper>
                            <p>{t("privacyPolicy.statement.text")}</p>
                        </TextWrapper>
                        <Button
                            onClick={() => {
                                openModal({
                                    children: <CookieConsentFormContainer setConsentData={setConsentData} />,
                                    disableCloseButton: true,
                                    fullWidth: true,
                                });
                            }}
                            outlined
                            style={
                                {
                                    "--btn-text": "#F8F6FE",
                                } as CSSProperties
                            }
                            variant="violet"
                        >
                            {t("privacyPolicy.statement.button")}
                        </Button>
                    </div>
                    <div className="space-y-6">
                        <h2 className="ui-typo-super-large">{t("privacyPolicy.aboutCookies.title")}</h2>
                        <TextWrapper className="space-y-8">
                            {t<string, string[]>("privacyPolicy.aboutCookies.text", {
                                returnObjects: true,
                            }).map((text, index) => (
                                <p key={index}>{text}</p>
                            ))}
                        </TextWrapper>
                    </div>
                    <div className="space-y-6">
                        <h2 className="ui-typo-super-large">{t("privacyPolicy.cookiesUsage.title")}</h2>
                        <TextWrapper>
                            <p>{t("privacyPolicy.cookiesUsage.text")}</p>
                        </TextWrapper>
                    </div>
                    <div className="space-y-6">
                        <h2 className="ui-typo-super-large">{t("privacyPolicy.consent.title")}</h2>
                        <TextWrapper>
                            <p>{t("privacyPolicy.consent.text")}</p>
                            <ul>
                                <li>
                                    <TextLink href="https://support.microsoft.com/cs-cz">
                                        Internet Explorer: support.microsoft.com
                                    </TextLink>
                                </li>
                                <li>
                                    <TextLink href="https://support.google.com/">
                                        Google Chrome: support.google.com
                                    </TextLink>
                                </li>
                                <li>
                                    <TextLink href="https://support.mozilla.org/cs/">
                                        Mozilla Firefox: support.mozilla.org
                                    </TextLink>
                                </li>
                                <li>
                                    <TextLink href="https://help.opera.com/cs/latest/">Opera: help.opera.com</TextLink>
                                </li>
                                <li>
                                    <TextLink href="https://support.apple.com/cs-cz">
                                        Safari: support.apple.com
                                    </TextLink>
                                </li>
                            </ul>
                        </TextWrapper>
                    </div>
                </div>
            </main>
            <Footer />
        </>
    );
};
